import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://weareflinch.com';
const SITE_NAME = 'FLINCH';
const DEFAULT_IMAGE = 'https://weareflinch.com/og-image.png';

/**
 * SEO Component for managing meta tags, Open Graph, Twitter Cards, and JSON-LD Schema
 * - Uses Organization schema on homepage
 * - Uses Article schema on insight/blog posts
 */
const SEO = ({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = 'website',
  article = null,
  noindex = false,
  jsonLd = null,
  isHomepage = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Strategic Marketing Partner`;
  const metaDescription = description || 'FLINCH is your strategic marketing partner. We bring clarity to chaos, personality to content, and outcomes that move the needle.';
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const imageUrl = image && image.startsWith('http') ? image : `${SITE_URL}${image || '/og-image.png'}`;

  // Organization Schema - Primary schema for homepage, secondary for other pages
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'FLINCH',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: 'https://customer-assets.emergentagent.com/job_c07ebe4f-8fac-4375-bd77-db80e53251b9/artifacts/3hq7a21m_flinch-logo-primary-dark.png',
      width: 512,
      height: 512,
    },
    description: 'Strategic marketing partner specializing in AI-powered business automation, personal branding, content strategy, and brand positioning. Smart Tech, Human Sense, Better Marketing.',
    foundingDate: '2024',
    slogan: 'Smart Tech, Human Sense, Better Marketing',
    sameAs: [
      'https://linkedin.com/company/weareflinch',
      'https://twitter.com/weareflinch',
      'https://instagram.com/weareflinch',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@weareflinch.com',
      availableLanguage: ['English'],
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
    },
  };

  // Website Schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    description: metaDescription,
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/insights?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  // Article Schema (for blog posts) - Schema.org compliant
  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${canonicalUrl}/#article`,
    headline: article.title,
    description: article.excerpt,
    image: {
      '@type': 'ImageObject',
      url: article.image || imageUrl,
      width: 1200,
      height: 630,
    },
    datePublished: convertDateToISO(article.datePublished),
    dateModified: convertDateToISO(article.dateModified || article.datePublished),
    author: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: 'https://customer-assets.emergentagent.com/job_c07ebe4f-8fac-4375-bd77-db80e53251b9/artifacts/3hq7a21m_flinch-logo-primary-dark.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    articleSection: article.category || 'Marketing Insights',
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    copyrightHolder: {
      '@id': `${SITE_URL}/#organization`,
    },
    copyrightYear: new Date().getFullYear(),
  } : null;

  // BreadcrumbList for articles
  const breadcrumbSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Insights',
        item: `${SITE_URL}/insights`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: canonicalUrl,
      },
    ],
  } : null;

  // Combine schemas based on page type
  const allSchemas = [];
  
  // Always include Organization schema
  allSchemas.push(organizationSchema);
  
  // Include Website schema on homepage
  if (isHomepage || canonical === '/') {
    allSchemas.push(websiteSchema);
  }
  
  // Include Article schema and breadcrumbs for blog posts
  if (articleSchema) {
    allSchemas.push(articleSchema);
    allSchemas.push(breadcrumbSchema);
  }
  
  // Include any custom JSON-LD
  if (jsonLd) allSchemas.push(jsonLd);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@weareflinch" />
      <meta name="twitter:creator" content="@weareflinch" />
      
      {/* Additional SEO Meta */}
      <meta name="author" content="FLINCH" />
      <meta name="publisher" content="FLINCH" />
      <meta name="keywords" content="marketing agency, personal branding, content strategy, brand positioning, marketing partner, brand strategy, thought leadership, AI marketing, business automation" />
      
      {/* JSON-LD Structured Data */}
      {allSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

/**
 * Helper function to convert date strings like "Dec 2024" to ISO format
 */
function convertDateToISO(dateStr) {
  if (!dateStr) return new Date().toISOString();
  
  // If already in ISO format, return as is
  if (dateStr.includes('-') || dateStr.includes('T')) {
    return dateStr;
  }
  
  // Parse "Dec 2024" or "December 2024" format
  const months = {
    'jan': '01', 'january': '01',
    'feb': '02', 'february': '02',
    'mar': '03', 'march': '03',
    'apr': '04', 'april': '04',
    'may': '05',
    'jun': '06', 'june': '06',
    'jul': '07', 'july': '07',
    'aug': '08', 'august': '08',
    'sep': '09', 'september': '09',
    'oct': '10', 'october': '10',
    'nov': '11', 'november': '11',
    'dec': '12', 'december': '12',
  };
  
  const parts = dateStr.toLowerCase().split(' ');
  if (parts.length >= 2) {
    const month = months[parts[0]] || '01';
    const year = parts[1] || new Date().getFullYear();
    return `${year}-${month}-01T00:00:00Z`;
  }
  
  return new Date().toISOString();
}

export default SEO;
